import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  ColunaDataList,
  DataList,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import ModalFiltrosCarteira, {
  FILTROS_CARTEIRA_VAZIOS,
  FiltrosCarteira,
  contarFiltrosCarteiraAtivos,
} from '../../../components/modal/filtros-carteira/ModalFiltrosCarteira';
import ModalDetalhesCarteira from '../../../components/modal/detalhes-carteira/ModalDetalhesCarteira';
import ModalInformarPlaca from '../../../components/modal/informar-placa/ModalInformarPlaca';
import { emplacarVeiculo } from '../../../api/carro/carroApi';
import { rotuloPlaca } from '../../../utils/veiculoZeroKm';

import {
  AssociadoDaCarteira,
  Carteira,
  VeiculoDaCarteira,
  atualizarCarteiraComSga,
  fetchCarteira,
} from '../../../api/carteira/carteiraApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { extrairMensagemErro } from '../../../utils/apiError';
import { decodeJwtPayload } from '../../../utils/jwt';
import { iniciarNovaAdesao } from '../../../utils/adesaoFluxo';
import { hidratarFluxoVistoria } from '../../../utils/vistoriaFluxo';
import { getUltimaVistoriaPorVeiculo } from '../../../api/vistoria/vistoriaApi';
import { clearCotacaoData } from '../../../store/cotacaoSlice';

import { ADESOES } from '../../adesoes/routes';
import { ASSOCIADO } from '../../associado/routes';
import { COTACAO } from '../../cotacao/routes';
import { HOME } from '../../home/routes';
import {
  VISTORIA,
  VISTORIA_ASSINATURA,
  VISTORIA_CAPTURA,
  VISTORIA_CHECKLIST_ACESSORIOS,
  VISTORIA_LAUDO,
  VISTORIA_LAUDO_HTML_VEICULO,
} from '../../vistoria/routes';

/**
 * `/carteira` — **Minha Carteira** (at-32; reescrita na at-65, frente D).
 *
 * A visão única do consultor sobre os próprios associados: uma linha por
 * associado, com o resumo da situação. Decisão de Victor (31/08/2026, rodada de
 * teste da at-32): **a tabela é um ÍNDICE enxuto** — a versão com os veículos
 * aninhados e todos os chips na linha tinha *"muita informação num lugar só"*. O
 * estado completo e as ações vivem no `ModalDetalhesCarteira`.
 *
 * ⚠️ **É a irmã do painel `/vistoria`** (reescrito na at-63): as duas listam a
 * carteira do mesmo consultor — uma por associado, a outra por veículo — e as duas
 * são portas de entrada do fluxo de vistoria. Elas **não podem divergir** sobre o
 * mesmo veículo, e é por isso que o texto e a cor do estado da vistoria vêm de
 * `utils/vistoriaFluxo.ts`, não de nenhuma das duas telas.
 *
 * ## O que mudou de comportamento na at-65
 *
 *   1. 🐛 **Falha ao carregar deixou de ser indistinguível de carteira vazia.** O
 *      `catch` dava `toast.error` (que passa) e a tabela mostrava *"Nenhum
 *      associado na sua carteira"* (que fica) — o consultor concluía que não tinha
 *      carteira. Virou `ErrorState` com "Tentar novamente" (spec 02 §9: *"spinner
 *      eterno e falha muda são o mesmo defeito"*).
 *   2. 🐛 **O carregamento não esconde mais a busca e os filtros.** Era
 *      `FullScreenLoader`, um overlay de tela cheia disparado **a cada paginação e
 *      a cada tecla digitada** — exatamente o que a spec 02 §2.7 proíbe.
 *   3. 🐛 **Três vazios distintos**: sem cadastros, a busca não achou, o filtro não
 *      casou. Eram o mesmo texto, e pedem ações opostas do consultor.
 *   4. 🔴 **Pendência deixou de ser LARANJA.** `.carteira-chip-alerta` usava
 *      `#ea5d0e`, **a cor da marca**, para "envio pendente" e "vistoria pendente" —
 *      e a spec 01 §2.1 reserva significado às cores. Pendência é âmbar.
 *   5. 🐛 **Fim do "Página 1 de 0"**: o `Math.ceil` saiu da tela para dentro do
 *      `Pagination`.
 *   6. **"Atualizar com o SGA" não cobre mais a tela.** A operação faz até ~150
 *      chamadas à Hinova e pode levar um minuto; o overlay deixava o consultor
 *      olhando um spinner sem saber o que esperar. Virou estado do botão mais uma
 *      nota — a mesma escolha da at-60, que deixou o loader de tela cheia só onde
 *      nada pode ser tocado.
 *   7. **O aviso dos totais saiu do `title`**, que não existe no celular (at-54).
 *
 * ⚠️ **Preservado:** a leitura do desfecho de `atualizarCarteiraComSga` pelo
 * RESUMO (o backend nunca devolve erro com o SGA fora do ar — devolve
 * `sgaIndisponivel`), a hidratação do Redux antes de navegar para a vistoria, o
 * preload de "Ver vistoria", o `clearCotacaoData` antes da cotação, os contadores
 * globais, o recorte de 3 placas na linha e a origem fora da tabela.
 */

const LIMITE_POR_PAGINA = 10;
const MAX_PLACAS_NA_LINHA = 3;

type EstadoLista = 'carregando' | 'ok' | 'erro';

const CarteiraPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const voltar = useVoltar(HOME());

  const accessToken = useSelector((state: any) => state.auth.accessToken);

  // Primeiro nome ao lado do título, como em `/adesoes`. Vem do JWT — o
  // `authSlice` não guarda o nome.
  const primeiroNome = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeJwtPayload(accessToken) as { nome?: string } | null;
    return payload?.nome?.trim().split(/\s+/)[0] ?? null;
  }, [accessToken]);

  const [dados, setDados] = useState<Carteira | null>(null);
  const [estadoLista, setEstadoLista] = useState<EstadoLista>('carregando');
  const [erro, setErro] = useState('');
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');

  const [filtros, setFiltros] = useState<FiltrosCarteira>(FILTROS_CARTEIRA_VAZIOS);
  const [filtrosAberto, setFiltrosAberto] = useState(false);
  const { origem, situacao } = filtros;
  const filtrosAtivos = contarFiltrosCarteiraAtivos(filtros);

  /** Associado com o modal de detalhes aberto (null = fechado). */
  const [detalhe, setDetalhe] = useState<AssociadoDaCarteira | null>(null);
  /** at-81: o veículo 0km cuja placa está sendo informada (e de quem é). */
  const [emplacando, setEmplacando] = useState<{
    veiculo: VeiculoDaCarteira;
    associado: AssociadoDaCarteira;
  } | null>(null);
  const [registrandoPlaca, setRegistrandoPlaca] = useState(false);
  /** Veículo cujo laudo está sendo pré-carregado — vira estado no botão dele. */
  const [abrindoVistoriaDe, setAbrindoVistoriaDe] = useState<string | null>(null);

  /** "Atualizar com o SGA" (at-32): conversa com a Hinova, então pode demorar. */
  const [atualizandoComSga, setAtualizandoComSga] = useState(false);

  /**
   * Só para repor o texto no `SearchInput` quando a TELA limpa a busca por conta
   * própria (ele guarda o próprio estado e só relê `valorInicial` quando a chave
   * muda). Mesmo mecanismo do painel `/vistoria`.
   */
  const [chaveBusca, setChaveBusca] = useState(0);

  const carregar = useCallback(async () => {
    setEstadoLista('carregando');
    try {
      const resposta = await fetchCarteira({
        page,
        limit: LIMITE_POR_PAGINA,
        busca,
        origem,
        situacao,
      });
      setDados(resposta);
      setEstadoLista('ok');
    } catch (e) {
      // ⚠️ A lista anterior é DESCARTADA: deixá-la na tela faria dado velho passar
      // por atual (lição do carimbo "Dados atualizados em", at-53).
      setDados(null);
      setErro(extrairMensagemErro(e, 'Não foi possível carregar sua carteira.'));
      setEstadoLista('erro');
    }
  }, [page, busca, origem, situacao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Confere a carteira com o SGA e importa o que faltar (at-32, Plano F).
   *
   * ⚠️ O desfecho é lido do **resumo**, e não de um `catch`: o backend não devolve
   * erro por SGA fora do ar — devolve `sgaIndisponivel` dentro do resumo, porque o
   * consultor precisa saber que **nada se perdeu**, não ver "erro".
   */
  const atualizarComSga = async () => {
    setAtualizandoComSga(true);
    try {
      const resumo = await atualizarCarteiraComSga();

      if (resumo.sgaIndisponivel) toast.warning(resumo.mensagem);
      else toast.success(resumo.mensagem);

      if (resumo.falhas.length > 0) {
        toast.warning(`${resumo.falhas.length} registro(s) não puderam ser trazidos do SGA.`);
      }

      // Veículo importado e divergência nova só aparecem na lista recarregada.
      await carregar();
    } catch (e) {
      toast.error(extrairMensagemErro(e, 'Não foi possível atualizar a carteira com o SGA.'));
    } finally {
      setAtualizandoComSga(false);
    }
  };

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPage(1);
  };

  const limparBusca = () => {
    setBusca('');
    setChaveBusca((n) => n + 1);
    setPage(1);
  };

  const aplicarFiltros = (novos: FiltrosCarteira) => {
    setFiltros(novos);
    setFiltrosAberto(false);
    // A página 3 do recorte anterior pode nem existir no novo.
    setPage(1);
  };

  /** Vistoria nova: o fluxo lê veículo/associado do Redux, então hidrata antes. */
  const iniciarVistoria = (associado: AssociadoDaCarteira, veiculo: VeiculoDaCarteira) => {
    if (!veiculo.podeVistoriar) return;

    hidratarFluxoVistoria(dispatch, montarItemVistoria(associado, veiculo));
    navigate(VISTORIA_CAPTURA());
  };

  /** Retomada: cai direto no primeiro passo pendente que o backend apontou. */
  const continuarVistoria = (associado: AssociadoDaCarteira, veiculo: VeiculoDaCarteira) => {
    if (!veiculo.vistoria) return;

    hidratarFluxoVistoria(dispatch, montarItemVistoria(associado, veiculo), veiculo.vistoria.id);

    const passo = veiculo.vistoria.proximoPasso;
    if (passo === 'CHECKLIST') {
      navigate(VISTORIA_CHECKLIST_ACESSORIOS());
      return;
    }
    if (passo === 'ASSINATURA') {
      navigate(VISTORIA_ASSINATURA(), {
        state: { associadoId: associado.id, carroId: veiculo.id },
      });
      return;
    }
    // LAUDO (ou concluída): a tela do laudo é onde o fluxo termina.
    navigate(VISTORIA_LAUDO());
  };

  /**
   * Visualização da vistoria concluída (somente leitura). O **preload** confirma
   * que a vistoria existe antes de navegar — sem ele a tela abriria vazia (at-24).
   * Preservado; o que mudou é que a espera virou estado do botão daquele veículo,
   * em vez de um loader cobrindo a tela inteira.
   */
  /**
   * Abre o "Informar placa" (at-81). ⚠️ Fecha o detalhe ANTES: o `Modal` do kit
   * prende Esc e Tab globalmente, e um modal sobre outro deixaria o foco escapar
   * para a página de trás (lição da at-70).
   */
  const abrirInformarPlaca = (veiculo: VeiculoDaCarteira) => {
    if (!detalhe) return;
    setEmplacando({ veiculo, associado: detalhe });
    setDetalhe(null);
  };

  const registrarPlaca = async (dados: { placa: string; renavam?: string }) => {
    if (!emplacando) return;
    setRegistrandoPlaca(true);
    try {
      const resultado = await emplacarVeiculo(emplacando.veiculo.id, dados);
      const status = resultado.envioSga?.status;
      if (status === 'PENDENTE') {
        toast.info('Placa registrada no SAAC. O SGA não respondeu agora — o envio será refeito automaticamente.');
      } else if (status === 'ENVIADO') {
        toast.success('Placa registrada e enviada ao SGA.');
      } else {
        toast.success('Placa registrada.');
      }
      setEmplacando(null);
      await carregar();
    } catch (e) {
      toast.error(extrairMensagemErro(e, 'Não foi possível registrar a placa.'));
    } finally {
      setRegistrandoPlaca(false);
    }
  };

  const verVistoria = async (veiculo: VeiculoDaCarteira) => {
    setAbrindoVistoriaDe(veiculo.id);
    try {
      await getUltimaVistoriaPorVeiculo({ vehicleId: veiculo.id });
      navigate(VISTORIA_LAUDO_HTML_VEICULO(veiculo.id));
    } catch (e) {
      toast.error(extrairMensagemErro(e, 'Falha ao abrir a vistoria deste veículo.'));
    } finally {
      setAbrindoVistoriaDe(null);
    }
  };

  const novaCotacao = () => {
    // Estado de cotação anterior não pode vazar para um fluxo novo (padrão da Home).
    dispatch(clearCotacaoData());
    navigate(COTACAO());
  };

  const novaAdesao = () => {
    // Sem a limpeza o formulário nasce com o que sobrou do fluxo anterior — e,
    // pior, herda o `rascunhoId` da adesão passada (at-38).
    iniciarNovaAdesao(dispatch);
    navigate(ASSOCIADO());
  };

  const itens = dados?.items ?? [];
  const totais = dados?.totais;
  const ocupado = estadoLista === 'carregando' || atualizandoComSga;

  const colunas: ColunaDataList<AssociadoDaCarteira>[] = [
    {
      id: 'nome',
      rotulo: 'Associado',
      principal: true,
      celula: (associado) => associado.nomeCompleto,
    },
    {
      id: 'cpf',
      rotulo: 'CPF/CNPJ',
      secundaria: true,
      celula: (associado) => (
        <span className="tw-tabular-nums">{formatCpfCnpj(String(associado.cpfCnpj || ''))}</span>
      ),
    },
    {
      id: 'veiculos',
      rotulo: 'Veículos',
      celula: (associado) => <ResumoVeiculos veiculos={associado.veiculos} />,
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (associado) => <ResumoSituacao associado={associado} />,
    },
  ];

  const conteudo = () => {
    if (estadoLista === 'carregando') return <Skeleton linhas={5} altura="tw-h-14" />;

    if (estadoLista === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar sua carteira."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (itens.length === 0) {
      /* 🔴 Os três vazios pedem ações DIFERENTES — a spec 02 §2.7 exige a
         distinção, e antes os três mostravam o mesmo texto. */
      if (busca) {
        return (
          <EmptyState
            titulo="Nenhum resultado para essa busca"
            descricao="A busca cobre nome, CPF, placa e modelo — e só o que já está no SAAC. Um veículo cadastrado direto no SGA precisa ser trazido primeiro."
            icone="busca"
            acao={
              <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
                <Botao variante="secundario" onClick={limparBusca}>
                  Limpar a busca
                </Botao>
                {/* A importação por placa vive no painel de vistoria (at-32) — é
                    para lá que se manda quem procurou e não achou, em vez de
                    duplicar aqui uma porta de entrada que já existe. */}
                <Botao variante="secundario" onClick={() => navigate(VISTORIA())}>
                  Buscar veículo no SGA
                </Botao>
              </div>
            }
          />
        );
      }

      if (filtrosAtivos > 0) {
        return (
          <EmptyState
            titulo="Nenhum associado com esses filtros"
            descricao="Nenhum registro da sua carteira casa com o recorte escolhido."
            acao={
              <Botao variante="secundario" onClick={() => aplicarFiltros(FILTROS_CARTEIRA_VAZIOS)}>
                Limpar filtros
              </Botao>
            }
          />
        );
      }

      return (
        <EmptyState
          titulo="Sua carteira está vazia"
          descricao="Os associados que você cadastrar — ou trouxer do SGA — aparecem aqui."
          acao={
            <Botao variante="primario" iconeEsquerda="mais" onClick={novaAdesao}>
              Nova Adesão
            </Botao>
          }
        />
      );
    }

    return (
      <>
        <DataList
          itens={itens}
          colunas={colunas}
          chave={(associado) => associado.id}
          rotuloAcoes="Detalhes"
          rotuloAcessivel="Associados da sua carteira e o andamento de cada um"
          acoes={(associado) => (
            <Botao
              variante="secundario"
              tamanho="sm"
              disabled={ocupado}
              title="Ver veículos, vistorias, produtos e ações deste associado"
              onClick={() => setDetalhe(associado)}
              className="tw-w-full md:tw-w-auto"
            >
              Detalhes
            </Botao>
          )}
        />

        <div className="tw-mt-4">
          <Pagination
            pagina={page}
            totalItens={dados?.totalItems ?? 0}
            porPagina={dados?.limit ?? LIMITE_POR_PAGINA}
            aoTrocar={setPage}
          />
        </div>
      </>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Minha Carteira"
          complemento={primeiroNome ?? undefined}
          aoVoltar={voltar}
          acaoDireita={
            <Botao
              variante="secundario"
              tamanho="md"
              iconeEsquerda="recarregar"
              carregando={atualizandoComSga}
              disabled={ocupado}
              onClick={() => void atualizarComSga()}
            >
              Atualizar com o SGA
            </Botao>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              {/* Materializa a limitação aceita da at-32: a API da Hinova não tem
                  "carteira por voluntário", então o que existe só no SGA e nunca
                  foi importado não aparece aqui. */}
              <Nota>
                Esta carteira mostra o que está no SAAC. Para trazer o que mudou no SGA —
                inclusive veículo novo de um associado seu —, use "Atualizar com o SGA".
              </Nota>

              {atualizandoComSga && (
                /* ⚠️ `aria-live` porque este texto APARECE enquanto a tela está
                   aberta; a nota acima é estática e não o leva (reanunciá-la a cada
                   render vizinho viraria ruído). */
                <Nota aria-live="polite">
                  Conferindo sua carteira com o SGA. Isso pode levar um minuto — a lista é
                  atualizada ao final, e nada se perde se o SGA estiver fora do ar.
                </Nota>
              )}

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <SearchInput
                  key={chaveBusca}
                  valorInicial={busca}
                  placeholder="Buscar por nome, CPF, placa ou modelo"
                  rotuloAcessivel="Buscar associado ou veículo na sua carteira"
                  className="tw-min-w-0 tw-flex-1 tw-basis-64"
                  aoBuscar={trocarBusca}
                />

                <Botao
                  variante="secundario"
                  tamanho="md"
                  iconeEsquerda="filtro"
                  disabled={ocupado}
                  aria-pressed={filtrosAtivos > 0}
                  onClick={() => setFiltrosAberto(true)}
                >
                  {filtrosAtivos > 0 ? `Filtros (${filtrosAtivos})` : 'Filtros'}
                </Botao>
              </div>

              {totais && (
                <div>
                  {/*
                    ⚠️ Os totais são GLOBAIS da carteira e não seguem a busca nem os
                    filtros (convenção at-10/at-28). O aviso vivia num `title`, que
                    **não existe no celular** — e aqui os chips têm linha própria,
                    então cabe dizê-lo por extenso.
                  */}
                  <p className="tw-m-0 tw-mb-1.5 tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
                    Totais da carteira — não mudam com a busca nem com os filtros
                  </p>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    <Chip variante="neutro">{totais.associados} associado(s)</Chip>
                    <Chip variante="neutro">{totais.veiculos} veículo(s)</Chip>
                    {totais.veiculosPendentesSga > 0 && (
                      <Chip variante="pendente">
                        {totais.veiculosPendentesSga} veículo(s) pendente(s) no SGA
                      </Chip>
                    )}
                    {totais.vistoriasPendentes > 0 && (
                      <Chip variante="pendente">
                        {totais.vistoriasPendentes} vistoria(s) a fazer
                      </Chip>
                    )}
                    {totais.importadosDoSga > 0 && (
                      <Chip variante="neutro">{totais.importadosDoSga} importado(s) do SGA</Chip>
                    )}
                  </div>
                </div>
              )}

              {conteudo()}
            </div>
          </div>
        </div>
      </div>

      <ModalFiltrosCarteira
        aberto={filtrosAberto}
        valor={filtros}
        onFechar={() => setFiltrosAberto(false)}
        onAplicar={aplicarFiltros}
      />

      <ModalDetalhesCarteira
        associado={detalhe}
        ocupado={ocupado}
        abrindoVistoriaDe={abrindoVistoriaDe}
        onFechar={() => setDetalhe(null)}
        onAdesao={() => navigate(ADESOES())}
        onNovaCotacao={novaCotacao}
        onIniciarVistoria={(veiculo) => detalhe && iniciarVistoria(detalhe, veiculo)}
        onContinuarVistoria={(veiculo) => detalhe && continuarVistoria(detalhe, veiculo)}
        onVerVistoria={(veiculo) => void verVistoria(veiculo)}
        onInformarPlaca={abrirInformarPlaca}
      />

      <ModalInformarPlaca
        aberto={emplacando !== null}
        descricao={
          emplacando
            ? `${emplacando.veiculo.modelo || 'Veículo'} de ${emplacando.associado.nomeCompleto}`
            : ''
        }
        processando={registrandoPlaca}
        onConfirmar={(dados) => void registrarPlaca(dados)}
        onCancelar={() => setEmplacando(null)}
      />
    </ShellSaac>
  );
};

/**
 * Placas resumidas na linha: até 3 e "+N" — o detalhe completo fica no modal.
 *
 * ⚠️ **Chamada duas vezes por item** (o `DataList` monta tabela e cards no mesmo
 * DOM), então precisa ser pura.
 */
const ResumoVeiculos: React.FC<{ veiculos: VeiculoDaCarteira[] }> = ({ veiculos }) => {
  if (veiculos.length === 0) {
    return <span className="tw-text-xs tw-text-texto-desabilitado">Nenhum veículo</span>;
  }

  // O veículo substituído aparece no modal como histórico, mas NÃO entra na
  // contagem da linha (at-37, item 39): "N veículo(s)" significa em circulação, e
  // é assim que os contadores globais do topo também contam.
  const emCirculacao = veiculos.filter((v) => !v.substituido);
  const substituidos = veiculos.length - emCirculacao.length;

  // at-81: o 0km aparece como "0km (sem placa)", não pela identificação provisória.
  const placas = emCirculacao.slice(0, MAX_PLACAS_NA_LINHA).map((v) => rotuloPlaca(v.placa));
  const excedente = emCirculacao.length - placas.length;

  return (
    <span className="tw-inline-flex tw-flex-col">
      <span className="tw-text-xs tw-text-texto-corpo">{emCirculacao.length} veículo(s)</span>
      <span className="tw-text-xs tw-text-texto-suave [overflow-wrap:anywhere]">
        {placas.length > 0 ? (
          <>
            {placas.join(', ')}
            {excedente > 0 && ` +${excedente}`}
          </>
        ) : (
          '—'
        )}
        {substituidos > 0 && (
          <span className="tw-text-amber-deep">
            {` · ${substituidos} substituído${substituidos > 1 ? 's' : ''}`}
          </span>
        )}
      </span>
    </span>
  );
};

/**
 * Resumo da situação do associado na linha: "Em dia" quando não há nada por
 * fazer, senão **um chip por FAMÍLIA** de pendência (envio ao SGA / vistoria). O
 * detalhamento fica no modal — aqui é só o suficiente para o consultor decidir
 * onde clicar.
 *
 * ⚠️ A oficina saiu daqui na at-53 (Victor, 08/09/2026: *"nada de oficina na
 * carteira"*): esta coluna fala só do que é pendência **do consultor** — envio ao
 * SGA e vistoria. O andamento na oficina, que é estado do veículo em outro
 * sistema, vive na tela Acompanhamento de Oficina.
 */
const ResumoSituacao: React.FC<{ associado: AssociadoDaCarteira }> = ({ associado }) => {
  const enviosPendentes =
    (associado.addedToSga ? 0 : 1) + associado.veiculos.filter((v) => !v.addedToSga).length;

  const vistoriasPendentes = associado.veiculos.filter(
    (v) => !v.vistoria || !v.vistoria.concluida,
  ).length;

  if (enviosPendentes === 0 && vistoriasPendentes === 0) {
    return <Chip variante="ok">Em dia</Chip>;
  }

  return (
    <span className="tw-flex tw-flex-wrap tw-gap-1.5">
      {enviosPendentes > 0 && (
        <Chip variante="pendente">{enviosPendentes} envio(s) pendente(s)</Chip>
      )}
      {vistoriasPendentes > 0 && (
        <Chip variante="pendente">{vistoriasPendentes} vistoria(s) pendente(s)</Chip>
      )}
    </span>
  );
};

/** Adapta a linha da carteira ao formato que o fluxo de vistoria espera. */
const montarItemVistoria = (associado: AssociadoDaCarteira, veiculo: VeiculoDaCarteira) => ({
  carroId: veiculo.id,
  placa: veiculo.placa,
  modelo: veiculo.modelo,
  anoModelo: veiculo.anoModelo,
  anoFabricacao: veiculo.anoFabricacao,
  tipoVeiculo: veiculo.tipoVeiculo,
  associado: {
    id: associado.id,
    nomeCompleto: associado.nomeCompleto,
    cpfCnpj: associado.cpfCnpj,
  },
});

export default CarteiraPage;
